'use client'

import type { OptionsType } from '@fukku/select/Select'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useGeolocation } from 'maps/hooks/useGeolocation/useGeolocation'
import { geocode } from 'maps/services/client/geocode/geocode'
import { type Location, MAP_TYPE, type Store } from 'maps/types'
import {
	filterAndSortByNearbyStore,
	refreshDistance,
} from 'maps/utils/distance/distance'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useEffect,
	useState,
} from 'react'
import { isRfidStoresType } from '../utils/mapTypes/mapTypes'

export type MapStateContextType = {
	type?: MAP_TYPE
	stores?: Store[]
	filteredStores?: Store[]
	storesBrand?: OptionsType[]
	selectedBrand?: string
	expanded?: Store
	onModal?: boolean
	searchInput?: string
	nearMe?: boolean
	location?: Location | null
	hasSearched?: boolean
	hasDistanceLimit?: boolean
	loading?: boolean
	fetchStores?: (location: Location, countryISO: string) => Promise<Store[]>
}

export type MapDispatchContextType = (
	state?: Partial<MapStateContextType>
) => void

export const MapStateContext = createContext<MapStateContextType>({})
export const MapDispatchContext = createContext<MapDispatchContextType>(() => {
	/* do nothing */
})

export interface MapProviderProps {
	readonly initialState?: MapStateContextType
}

export function MapProvider({
	initialState,
	children,
}: PropsWithChildren<MapProviderProps>): React.ReactNode {
	const {
		country: { countryName, languageISO, countryISO },
	} = useMasterData()
	const { t } = useLabels()
	const { location, isLoading: isGeoLocationLoading } = useGeolocation()

	const defaultInitialState: MapStateContextType = {
		type: MAP_TYPE.STORES,
		stores: [],
		filteredStores: [],
		expanded: initialState?.stores?.[0],
		onModal: false,
		storesBrand: initialState?.storesBrand,
		selectedBrand: initialState?.selectedBrand,
		searchInput: '',
		hasSearched: false,
		hasDistanceLimit: false,
		loading: false,
		nearMe: false,
		location,
	}

	const [state, setState] = useState({
		...defaultInitialState,
		...initialState,
	})

	const dispatch = useCallback(
		(newState?: MapStateContextType) =>
			setState((prevState) => ({ ...prevState, ...newState })),
		[]
	)

	// Asks for current position on load. Only used for modal stores finder.
	// TODO - This should be moved to the only one StoreLocator modal component
	useEffect(() => {
		if (state.nearMe) {
			const getStores = async () => {
				const geolocation =
					location ||
					(await geocode({
						languageISO,
						countryISO,
					}))
				const filteredStoresNearMe =
					state.stores && geolocation
						? filterAndSortByNearbyStore(
								refreshDistance(state.stores, geolocation),
								state.hasDistanceLimit
							)
						: undefined

				let searchInput = state.searchInput
				if (!isRfidStoresType(state.type)) {
					searchInput = location
						? t('storeLocator.searchBox.nearMe.input')
						: countryName
				}

				setState((prevState) => ({
					...prevState,
					location,
					searchInput,
					loading: isGeoLocationLoading,
					expanded: filteredStoresNearMe?.[0],
					filteredStores: filteredStoresNearMe,
				}))
			}

			void getStores()
		}
	}, [location, isGeoLocationLoading])

	return (
		<MapStateContext.Provider value={state}>
			<MapDispatchContext.Provider value={dispatch}>
				{children}
			</MapDispatchContext.Provider>
		</MapStateContext.Provider>
	)
}
